import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Cable,
  Link as LinkIcon,
  NetworkIcon,
  Pencil,
  Plus,
  Power,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { agentsApi } from "@/features/agents/api";
import {
  phoneNumbersApi,
  sipTrunksApi,
  type PhoneNumber,
  type SipTrunk,
  type SipTrunkPayload,
} from "@/features/telephony/api";
import { cn } from "@/lib/utils";

const KEY = ["sip-trunks"] as const;
const NONE = "__none__";

type FormState = {
  name: string;
  description: string;
  phone_number_mode: "new" | "existing";
  phone_number: string;
  new_did: string;
  new_did_label: string;
  inbound_agent: string;
  termination_uri: string;
  sip_username: string;
  sip_password: string;
  transport: "tcp" | "udp" | "tls";
  allowed_inbound_ips: string;
  codecs: string;
  max_concurrent_calls: number;
  status: "active" | "inactive";
};

const EMPTY_FORM: FormState = {
  name: "",
  description: "",
  phone_number_mode: "new",
  phone_number: "",
  new_did: "",
  new_did_label: "",
  inbound_agent: "",
  termination_uri: "",
  sip_username: "",
  sip_password: "",
  transport: "tcp",
  allowed_inbound_ips: "",
  codecs: "PCMU, PCMA",
  max_concurrent_calls: 10,
  status: "active",
};

export default function SipTrunksPage() {
  const role = useAuthStore((s) => s.user?.role ?? "member");
  const isAdmin = hasMinRole(role, "admin");
  const qc = useQueryClient();

  const trunksQ = useQuery({
    queryKey: [...KEY, "list"],
    queryFn: sipTrunksApi.list,
    staleTime: 30_000,
  });
  const numbersQ = useQuery({
    queryKey: ["phone-numbers", "list"],
    queryFn: phoneNumbersApi.list,
    staleTime: 30_000,
  });
  const agentsQ = useQuery({
    queryKey: ["agents", "list"],
    queryFn: agentsApi.list,
    staleTime: 60_000,
  });

  const trunks = trunksQ.data ?? [];
  const numbers = numbersQ.data ?? [];
  const agents = agentsQ.data ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<SipTrunk | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SipTrunk | null>(null);
  const [webhookTarget, setWebhookTarget] = useState<SipTrunk | null>(null);
  const [testResult, setTestResult] = useState<{
    id: string;
    ok: boolean;
    message: string;
  } | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    qc.invalidateQueries({ queryKey: ["phone-numbers", "list"] });
  }

  function openCreate() {
    setEditTarget(null);
    setDialogOpen(true);
  }

  function openEdit(t: SipTrunk) {
    setEditTarget(t);
    setDialogOpen(true);
  }

  const testM = useMutation({
    mutationFn: (id: string) => sipTrunksApi.testConnection(id),
    onSuccess: (data, id) =>
      setTestResult({ id, ok: data.ok, message: data.message }),
    onError: (err, id) => {
      const e = err as { response?: { data?: { errors?: string[] } } };
      setTestResult({
        id,
        ok: false,
        message: (e?.response?.data?.errors ?? ["Test failed"]).join(" | "),
      });
    },
  });

  const toggleM = useMutation({
    mutationFn: (id: string) => sipTrunksApi.toggleStatus(id),
    onSuccess: () => {
      toast.info("Trunk status toggled");
      invalidate();
    },
    onError: () => toast.error("Failed to toggle status"),
  });

  const removeM = useMutation({
    mutationFn: (id: string) => sipTrunksApi.remove(id),
    onSuccess: () => {
      toast.success("SIP trunk deleted");
      invalidate();
    },
    onError: () => toast.error("Failed to delete SIP trunk"),
  });

  function agentName(phoneId: string | null) {
    if (!phoneId) return null;
    const pn = numbers.find((p) => p.id === phoneId);
    if (!pn?.inbound_agent) return null;
    return (
      agents.find((a) => a.id === pn.inbound_agent)?.name ||
      pn.inbound_agent_name ||
      null
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-md bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400">
            <Cable className="size-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">SIP Trunks</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Connect to SIP carriers for inbound and outbound calling.
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={openCreate}>
            <Plus className="size-4" />
            Add SIP Trunk
          </Button>
        )}
      </div>

      {testResult && (
        <div
          className={cn(
            "flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-sm",
            testResult.ok
              ? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400"
              : "border-destructive/30 bg-destructive/10 text-destructive",
          )}
        >
          <span>{testResult.message}</span>
          <button
            type="button"
            onClick={() => setTestResult(null)}
            className="text-xs underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      )}

      {trunksQ.isLoading && (
        <Card>
          <CardContent className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {!trunksQ.isLoading && trunks.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Cable className="size-8 text-muted-foreground" />
            <p className="text-base font-medium">No SIP trunks configured</p>
            <p className="max-w-md text-sm text-muted-foreground">
              Click "Add SIP Trunk" to connect your own carrier (Telnyx,
              Vonage, Twilio Elastic SIP, or any SIP provider) and add your
              phone numbers (DIDs) for inbound calls.
            </p>
          </CardContent>
        </Card>
      )}

      {!trunksQ.isLoading && trunks.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Termination URI</th>
                    <th className="px-4 py-3 font-medium">Transport</th>
                    <th className="px-4 py-3 font-medium">DID</th>
                    <th className="px-4 py-3 font-medium">Inbound Agent</th>
                    <th className="px-4 py-3 font-medium">Max</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Calls</th>
                    <th className="px-4 py-3 text-right font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {trunks.map((t) => (
                    <tr key={t.id} className="hover:bg-accent/40">
                      <td className="px-4 py-3">
                        <p className="font-medium">{t.name}</p>
                        {t.description && (
                          <p className="text-xs text-muted-foreground">
                            {t.description}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {t.termination_uri}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs uppercase">
                          {t.transport}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {t.phone_number_display || (
                          <span className="text-muted-foreground">No DID</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {agentName(t.phone_number) || (
                          <span className="text-muted-foreground">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3 tabular-nums">
                        {t.max_concurrent_calls}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                            t.status === "active"
                              ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400"
                              : "text-muted-foreground bg-muted",
                          )}
                        >
                          {t.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 tabular-nums">
                        {t.total_calls}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            onClick={() => setWebhookTarget(t)}
                            aria-label="Webhook setup"
                          >
                            <LinkIcon className="size-4" />
                          </Button>
                          {isAdmin && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 text-blue-600 hover:text-blue-600 dark:text-blue-400"
                                onClick={() => testM.mutate(t.id)}
                                disabled={
                                  testM.isPending && testM.variables === t.id
                                }
                                aria-label="Test URI"
                              >
                                <NetworkIcon className="size-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "size-8",
                                  t.status === "active"
                                    ? "text-amber-600 hover:text-amber-600 dark:text-amber-400"
                                    : "text-emerald-600 hover:text-emerald-600 dark:text-emerald-400",
                                )}
                                onClick={() => toggleM.mutate(t.id)}
                                aria-label="Toggle status"
                              >
                                <Power className="size-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8"
                                onClick={() => openEdit(t)}
                                aria-label="Edit"
                              >
                                <Pencil className="size-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 text-destructive hover:text-destructive"
                                onClick={() => setDeleteTarget(t)}
                                aria-label="Delete"
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {dialogOpen && (
        <TrunkDialog
          open={dialogOpen}
          editTarget={editTarget}
          numbers={numbers}
          trunks={trunks}
          agents={agents}
          onOpenChange={setDialogOpen}
          onSaved={invalidate}
        />
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SIP trunk</AlertDialogTitle>
            <AlertDialogDescription>
              Delete{" "}
              <span className="font-medium text-foreground">
                {deleteTarget?.name}
              </span>
              ? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeM.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={removeM.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) removeM.mutate(deleteTarget.id);
                setDeleteTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeM.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {webhookTarget && (
        <WebhookDialog
          trunk={webhookTarget}
          onClose={() => setWebhookTarget(null)}
        />
      )}
    </div>
  );
}

function TrunkDialog({
  open,
  editTarget,
  numbers,
  trunks,
  agents,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  editTarget: SipTrunk | null;
  numbers: PhoneNumber[];
  trunks: SipTrunk[];
  agents: { id: string; name: string }[];
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (editTarget) {
      const pn = numbers.find((p) => p.id === editTarget.phone_number);
      setForm({
        name: editTarget.name,
        description: editTarget.description || "",
        phone_number_mode: editTarget.phone_number ? "existing" : "new",
        phone_number: editTarget.phone_number || "",
        new_did: "",
        new_did_label: "",
        inbound_agent: pn?.inbound_agent || "",
        termination_uri: editTarget.termination_uri,
        sip_username: editTarget.sip_username,
        sip_password: "",
        transport: editTarget.transport,
        allowed_inbound_ips: (editTarget.allowed_inbound_ips || []).join("\n"),
        codecs: (editTarget.codecs || []).join(", "),
        max_concurrent_calls: editTarget.max_concurrent_calls,
        status: editTarget.status,
      });
    } else {
      setForm({ ...EMPTY_FORM });
    }
    setError("");
  }, [open, editTarget, numbers]);

  const availableNumbers = useMemo(() => {
    return numbers.filter((pn) => {
      if (pn.status === "released") return false;
      const linked = trunks.find((t) => t.phone_number === pn.id);
      return !linked || (editTarget && linked.id === editTarget.id);
    });
  }, [numbers, trunks, editTarget]);

  const save = useMutation({
    mutationFn: async () => {
      let phoneNumberId: string | null = form.phone_number || null;

      if (form.phone_number_mode === "new" && form.new_did.trim()) {
        const did = form.new_did.trim();
        if (!did.startsWith("+")) {
          throw new Error("Phone number must be in E.164 format (e.g. +14155551234).");
        }
        const pn = await phoneNumbersApi.create({
          number: did,
          formatted_number: did,
          label: form.new_did_label.trim() || "",
          provider: "imported",
          status: "active",
          inbound_agent: form.inbound_agent || null,
        });
        phoneNumberId = pn.id;
      } else if (
        form.phone_number_mode === "existing" &&
        form.phone_number
      ) {
        const linkedPN = numbers.find((pn) => pn.id === form.phone_number);
        const currentAgent = linkedPN?.inbound_agent || "";
        if (form.inbound_agent !== currentAgent) {
          try {
            await phoneNumbersApi.update(form.phone_number, {
              inbound_agent: form.inbound_agent || null,
            });
          } catch {
            // non-fatal
          }
        }
      }

      const payload: SipTrunkPayload = {
        name: form.name,
        description: form.description,
        phone_number: phoneNumberId,
        termination_uri: form.termination_uri,
        sip_username: form.sip_username,
        transport: form.transport,
        allowed_inbound_ips: form.allowed_inbound_ips
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        codecs: form.codecs
          .split(",")
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
        max_concurrent_calls: Number(form.max_concurrent_calls),
        status: form.status,
      };
      if (form.sip_password) payload.sip_password = form.sip_password;

      if (editTarget) {
        await sipTrunksApi.update(editTarget.id, payload);
      } else {
        await sipTrunksApi.create(payload);
      }
    },
    onSuccess: () => {
      toast.success(editTarget ? "SIP trunk updated" : "SIP trunk created");
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => {
      const e = err as { message?: string; response?: { data?: unknown } };
      const data = e?.response?.data as Record<string, unknown> | undefined;
      const msg =
        typeof data === "string"
          ? data
          : (data?.error as { message?: string } | undefined)?.message ||
            (typeof data?.error === "string" ? data.error : "") ||
            (data?.message as string | undefined) ||
            (data?.detail as string | undefined) ||
            e?.message ||
            "Failed to save SIP trunk";
      setError(String(msg));
    },
  });

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editTarget ? "Edit SIP Trunk" : "Add SIP Trunk"}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError("");
            save.mutate();
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="trunk-name">Name</Label>
            <Input
              id="trunk-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              autoFocus
              placeholder="e.g. Telnyx US East"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="trunk-desc">Description</Label>
            <Input
              id="trunk-desc"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </div>

          <div className="border-t pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              SIP Endpoint
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="termination">Termination URI</Label>
            <Input
              id="termination"
              value={form.termination_uri}
              onChange={(e) => set("termination_uri", e.target.value)}
              placeholder="sip.telnyx.com  |  sip:user@203.0.113.5:5060"
              className="font-mono"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sip-user">SIP Username</Label>
              <Input
                id="sip-user"
                value={form.sip_username}
                onChange={(e) => set("sip_username", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sip-pass">
                {editTarget
                  ? "New Password (leave blank to keep)"
                  : "SIP Password"}
              </Label>
              <Input
                id="sip-pass"
                type="password"
                value={form.sip_password}
                onChange={(e) => set("sip_password", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Transport</Label>
            <Select
              value={form.transport}
              onValueChange={(v) => set("transport", v as FormState["transport"])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tcp">TCP</SelectItem>
                <SelectItem value="udp">UDP</SelectItem>
                <SelectItem value="tls">TLS / SIPS</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border-t pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Phone Number (DID)
            </p>
            <p className="text-xs text-muted-foreground">
              Link a phone number to this trunk for inbound call routing.
            </p>
          </div>

          <div className="flex gap-4 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={form.phone_number_mode === "new"}
                onChange={() => {
                  set("phone_number_mode", "new");
                  set("phone_number", "");
                }}
              />
              Add new DID
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={form.phone_number_mode === "existing"}
                onChange={() => {
                  set("phone_number_mode", "existing");
                  set("new_did", "");
                }}
              />
              Use existing number
            </label>
          </div>

          {form.phone_number_mode === "new" ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="new-did">Phone Number (E.164)</Label>
                <Input
                  id="new-did"
                  value={form.new_did}
                  onChange={(e) => set("new_did", e.target.value)}
                  placeholder="+14155551234"
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-did-label">Label (optional)</Label>
                <Input
                  id="new-did-label"
                  value={form.new_did_label}
                  onChange={(e) => set("new_did_label", e.target.value)}
                  placeholder="e.g. Telnyx US Main"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Phone Number</Label>
              <Select
                value={form.phone_number || NONE}
                onValueChange={(v) =>
                  set("phone_number", v === NONE ? "" : v)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>
                    <em>None</em>
                  </SelectItem>
                  {availableNumbers.map((pn) => (
                    <SelectItem key={pn.id} value={pn.id}>
                      {pn.formatted_number || pn.number}
                      {pn.label ? ` (${pn.label})` : ""}
                      {pn.provider === "twilio"
                        ? " [Twilio]"
                        : pn.provider === "imported"
                        ? " [Imported]"
                        : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {((form.phone_number_mode === "new" && form.new_did.trim()) ||
            (form.phone_number_mode === "existing" && form.phone_number)) && (
            <div className="space-y-1.5">
              <Label>Inbound Agent</Label>
              <Select
                value={form.inbound_agent || NONE}
                onValueChange={(v) =>
                  set("inbound_agent", v === NONE ? "" : v)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>
                    <em>None</em>
                  </SelectItem>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The agent that will handle inbound calls to this number.
              </p>
            </div>
          )}

          <div className="border-t pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Capacity & Security
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="max-calls">Max Concurrent Calls</Label>
            <Input
              id="max-calls"
              type="number"
              min={1}
              max={1000}
              value={form.max_concurrent_calls}
              onChange={(e) =>
                set("max_concurrent_calls", Number(e.target.value))
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ips">Allowed Inbound IPs (one CIDR per line)</Label>
            <Textarea
              id="ips"
              rows={3}
              value={form.allowed_inbound_ips}
              onChange={(e) => set("allowed_inbound_ips", e.target.value)}
              placeholder="Leave empty to allow all IPs. Example: 192.0.2.0/24"
              className="font-mono text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="codecs">Codecs (comma-separated)</Label>
            <Input
              id="codecs"
              value={form.codecs}
              onChange={(e) => set("codecs", e.target.value)}
              placeholder="PCMU, PCMA, G729, opus"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select
              value={form.status}
              onValueChange={(v) => set("status", v as FormState["status"])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                save.isPending ||
                !form.name.trim() ||
                !form.termination_uri.trim()
              }
            >
              {save.isPending
                ? "Saving…"
                : editTarget
                ? "Save Changes"
                : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function WebhookDialog({
  trunk,
  onClose,
}: {
  trunk: SipTrunk;
  onClose: () => void;
}) {
  const base = useMemo(() => window.location.origin, []);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Webhook Setup — {trunk.name}</DialogTitle>
          <DialogDescription>
            Configure your SIP provider to send call webhooks to one of these
            URLs. Linked DID:{" "}
            <strong className="text-foreground">
              {trunk.phone_number_display || "None"}
            </strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <WebhookBlock
            label="Twilio (Voice Webhook URL):"
            url={`${base}/api/v1/telephony/incoming/voice/`}
          />
          <WebhookBlock
            label="Twilio (Status Callback URL):"
            url={`${base}/api/v1/telephony/incoming/status/`}
          />
          <div className="border-t pt-3" />
          <WebhookBlock
            label="Telnyx (Webhook URL):"
            url={`${base}/api/v1/telephony/incoming/telnyx/`}
            hint="In Telnyx Portal: Connection settings → Webhooks → set this URL for call events."
          />
          <WebhookBlock
            label="Vonage / Nexmo (Answer URL):"
            url={`${base}/api/v1/telephony/incoming/vonage/`}
            hint="In Vonage Dashboard: Numbers → Your number → Answer URL (HTTP POST)."
          />
          <WebhookBlock
            label="Generic SIP / Other Providers:"
            url={`${base}/api/v1/telephony/incoming/sip/`}
            hint="Accepts JSON POST with fields: call_id, from, to. Works with any SIP provider that supports HTTP webhooks."
          />
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WebhookBlock({
  label,
  url,
  hint,
}: {
  label: string;
  url: string;
  hint?: string;
}) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">{label}</p>
      <div className="flex items-center gap-2">
        <pre className="flex-1 overflow-x-auto rounded-md bg-muted px-2 py-1.5 font-mono text-xs">
          {url}
        </pre>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(url);
            toast.success("Copied");
          }}
        >
          Copy
        </Button>
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
